<template>
  <!--
    The frame for the pages people see before (or instead of) the app:
    sign in, sign up, onboarding, legal pages. It echoes the app shell: the
    brand bar on top, the current path as a prompt, then one card. Pages set
    `definePageMeta({ authWidth: 'narrow' })` for a short form.
  -->
  <div class="kb-auth">
    <header class="kb-auth-bar">
      <NuxtLink
        to="/"
        class="flex items-center gap-2 min-w-0"
        aria-label="Krewebacchus home"
      >
        <img
          src="~/assets/images/logo.svg"
          alt=""
          class="h-[28px] w-auto [filter:var(--kb-logo-filter)]"
        />
        <span class="kb-wordmark text-[15px]">krewebacchus</span>
      </NuxtLink>
      <span
        v-if="isStaging"
        class="kb-tag kb-tag-warn"
      >staging</span>

      <div class="ml-auto flex items-center gap-1">
        <button
          v-if="isAuthenticated"
          v-tooltip.bottom="'Sign out'"
          type="button"
          class="kb-auth-icon"
          aria-label="Sign out"
          @click="handleLogout"
        >
          <i class="pi pi-sign-out" />
        </button>
        <KbThemeSwitch />
      </div>
    </header>

    <main
      class="kb-auth-main"
      :class="{ 'is-narrow': narrow }"
    >
      <div
        class="kb-auth-prompt"
        aria-hidden="true"
      >
        <b>krewe</b> {{ promptPath }}<span class="kb-caret" />
      </div>

      <div class="kb-auth-card">
        <ClientOnly>
          <slot />
        </ClientOnly>
      </div>

      <footer class="kb-auth-footer">
        <span>© {{ year }} Intergalactic Krewe of Chewbacchus</span>
        <span>
          <NuxtLink to="/privacy-policy">Privacy policy</NuxtLink>
          <span aria-hidden="true"> · </span>
          <NuxtLink to="/terms-of-service">Terms of service</NuxtLink>
        </span>
      </footer>
    </main>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useCurrentUser } from 'vuefire'
import { useToast } from 'primevue/usetoast'
import { useAuth } from '~/composables/useAuth'

const runtimeConfig = useRuntimeConfig()
const currentUser = useCurrentUser()
const route = useRoute()
const toast = useToast()
const { signOutUser } = useAuth()

const isAuthenticated = computed(() => !!currentUser.value)

const isStaging = computed(() => runtimeConfig.public.krewebacchusEnv === 'staging')

// A short form (sign in, sign up) gets a narrow card
const narrow = computed(() => route.meta.authWidth === 'narrow')

const promptPath = computed(() => (route.path === '/' ? '~' : `~${route.path}`))

const year = new Date().getFullYear()

const handleLogout = async () => {
  try {
    // Reloads into /login, which says they're signed out
    const result = await signOutUser('signed-out')
    if (result && !result.success) throw result.error
  }
  catch (error) {
    console.error('Logout error:', error)
    toast.add({
      severity: 'error',
      summary: 'Sign out failed',
      detail: 'Something went wrong while signing out',
      life: 3000,
    })
  }
}
</script>

<style scoped>
.kb-auth {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  min-height: 100dvh;
  background-color: var(--kb-bg);
  /* A faint accent glow from the top, the console's "screen" */
  background-image: radial-gradient(ellipse 80% 40% at 50% 0%, var(--kb-accent-wash), transparent 70%);
}

.kb-auth-bar {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  height: 52px;
  padding: 0 1rem;
  border-bottom: 1px solid var(--kb-line);
}

@media (min-width: 640px) {
  .kb-auth-bar {
    padding: 0 1.25rem;
  }
}

.kb-auth-icon {
  display: grid;
  place-items: center;
  width: 2rem;
  height: 2rem;
  border-radius: 3px;
  color: var(--kb-dim);
}

.kb-auth-icon:hover {
  color: var(--kb-fg);
  background: var(--kb-raised);
}

.kb-auth-icon:focus-visible {
  outline: 2px solid var(--kb-accent);
  outline-offset: 1px;
}

.kb-auth-main {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  flex: 1;
  width: 100%;
  max-width: 46rem;
  margin: 0 auto;
  padding: 2rem 1rem 1.5rem;
}

.kb-auth-main.is-narrow {
  max-width: 30rem;
}

@media (min-width: 640px) {
  .kb-auth-main {
    padding-top: 3.5rem;
  }
}

.kb-auth-prompt {
  overflow: hidden;
  font: 13px var(--kb-font-mono);
  color: var(--kb-muted);
  white-space: nowrap;
  text-overflow: ellipsis;
}

.kb-auth-prompt b {
  font-weight: 500;
  color: var(--kb-accent);
}

.kb-auth-card {
  padding: 1.5rem 1.25rem;
  border: 1px solid var(--kb-line);
  border-radius: 3px;
  background: var(--kb-pane);
}

@media (min-width: 640px) {
  .kb-auth-card {
    padding: 2rem;
  }
}

.kb-auth-footer {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 0.25rem 1rem;
  padding-top: 0.25rem;
  font: 12px var(--kb-font-mono);
  color: var(--kb-dim);
}

.kb-auth-footer a {
  color: var(--kb-muted);
}

.kb-auth-footer a:hover {
  color: var(--kb-accent);
}
</style>
